/**
 * Forgot Password Page
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { useRequestPasswordReset } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export function ForgotPasswordPage() {
    const requestReset = useRequestPasswordReset();
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        requestReset.mutate(email, {
            onSuccess: () => setSubmitted(true),
        });
    };

    if (submitted) {
        return (
            <div className="space-y-4 text-center">
                <Alert className="bg-green-50 border-green-200 text-green-800">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription>
                        <h2 className="font-semibold">Check your email</h2>
                        <p className="mt-1 text-sm text-green-700">
                            If an account exists with {email}, you will receive a password reset link shortly.
                        </p>
                    </AlertDescription>
                </Alert>
                <Link
                    to="/auth/login"
                    className="text-sm text-primary hover:underline"
                >
                    Back to login
                </Link>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center">
                <h2 className="text-lg font-semibold text-foreground">Reset your password</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    Enter your email address and we'll send you a link to reset your password.
                </p>
            </div>

            {requestReset.error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        {requestReset.error instanceof Error ? requestReset.error.message : 'An error occurred'}
                    </AlertDescription>
                </Alert>
            )}

            <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                />
            </div>

            <Button
                type="submit"
                disabled={requestReset.isPending}
                className="w-full"
            >
                {requestReset.isPending ? 'Sending...' : 'Send reset link'}
            </Button>

            <div className="text-center text-sm">
                <Link
                    to="/auth/login"
                    className="text-primary hover:underline"
                >
                    Back to login
                </Link>
            </div>
        </form>
    );
}
